import { useEffect, useMemo, useState } from 'react';

import { API_BASE_PATH } from '../../apiConfig';
import type { RunIssueAcceptanceStatus } from './appRunData';
import { AppPentestProgressWidget } from './AppChatWidgets';
import { AppSeverity, AppStatusPill, ISSUE_STATUS_LABELS, formatScanDate } from './AppIssuePanel';
import { IconChat, IconCheckCircle, IconClose, IconDismiss, IconDownload, IconZap } from './appIcons';
import type { IssueRegister, RegisterIssue, RegisterStatus } from './useWorkspaceIssues';

type StatusTab = RegisterStatus | 'all';

const STATUS_TABS: StatusTab[] = ['open', 'acknowledged', 'fixed', 'dismissed', 'all'];
// Open and All are always shown; the other tabs appear once they hold something.
const PINNED_TABS = new Set<StatusTab>(['open', 'all']);
const SEVERITY_FILTERS: { id: RegisterIssue['severity']; label: string }[] = [
  { id: 'critical', label: 'Critical' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
  { id: 'informational', label: 'Info' }
];
const ACTIVE_SCAN_STATUSES = ['queued', 'pending', 'running', 'started'];
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60]
];
const relativeTime = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

const formatRelative = (iso: string): string => {
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const [unit, size] = RELATIVE_UNITS.find(([, unitSeconds]) => Math.abs(seconds) >= unitSeconds) ?? ['minute', 60];

  return relativeTime.format(Math.round(seconds / size), unit);
};

const getReportPdfUrl = (scanId: string): string => `${API_BASE_PATH}/runs/${encodeURIComponent(scanId)}/issues.pdf`;

interface AppIssuesViewProps {
  register: IssueRegister;
  scanFilter: string | null;
  selectedKey: string | null;
  onScanFilterChange: (scanId: string | null) => void;
  onSelect: (key: string) => void;
  onSetStatus: (keys: string[], status: RunIssueAcceptanceStatus | null) => Promise<void>;
  onAsk: (issues: RegisterIssue[]) => void;
  onRunPentest: () => void;
  onScanFinished: () => void;
  onRetry: () => void;
}

function AppIssuesView({
  register,
  scanFilter,
  selectedKey,
  onScanFilterChange,
  onSelect,
  onSetStatus,
  onAsk,
  onRunPentest,
  onScanFinished,
  onRetry
}: AppIssuesViewProps) {
  const [statusTab, setStatusTab] = useState<StatusTab>('open');
  const [severity, setSeverity] = useState('');
  const [query, setQuery] = useState('');
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(() => new Set());
  const [bulkStatus, setBulkStatus] = useState<RunIssueAcceptanceStatus | 'open' | null>(null);
  const [bulkError, setBulkError] = useState('');
  const latestScan = register.scans[0];
  const isScanActive = Boolean(latestScan && ACTIVE_SCAN_STATUSES.includes(latestScan.status));

  // Severity, scan and search narrow every tab; the tab counts reflect them.
  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();

    return register.issues.filter((issue) => {
      return (!scanFilter || issue.occurrences.some((occurrence) => occurrence.scanId === scanFilter))
        && (!severity || issue.severity === severity)
        && (!search || `${issue.cleanTitle} ${issue.files.join(' ')}`.toLowerCase().includes(search));
    });
  }, [query, register.issues, scanFilter, severity]);

  const counts = useMemo(() => {
    const result: Record<StatusTab, number> = { open: 0, acknowledged: 0, fixed: 0, dismissed: 0, all: filtered.length };

    filtered.forEach((issue) => {
      result[issue.status] += 1;
    });

    return result;
  }, [filtered]);

  const visible = statusTab === 'all' ? filtered : filtered.filter((issue) => issue.status === statusTab);
  const checkedIssues = visible.filter((issue) => checkedKeys.has(issue.key));
  const isAllChecked = visible.length > 0 && checkedIssues.length === visible.length;
  const presentSeverities = SEVERITY_FILTERS.filter((filter) => {
    return register.issues.some((issue) => issue.severity === filter.id);
  });
  const hasFilters = Boolean(scanFilter) || Boolean(severity) || query.trim().length > 0;
  const shownTabs = STATUS_TABS.filter((tab) => PINNED_TABS.has(tab) || tab === statusTab || counts[tab] > 0);
  const pdfScanId = scanFilter ?? register.reportScans[0]?.id;

  // Selection only applies to what is on screen.
  useEffect(() => {
    setCheckedKeys(new Set());
    setBulkError('');
  }, [statusTab, scanFilter, severity, query, register.workspaceId]);

  const toggleChecked = (key: string) => {
    setCheckedKeys((current) => {
      const next = new Set(current);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const applyBulk = (status: RunIssueAcceptanceStatus | null) => {
    setBulkStatus(status ?? 'open');
    setBulkError('');
    onSetStatus(checkedIssues.map((issue) => issue.key), status)
      .then(() => setCheckedKeys(new Set()))
      .catch((error: unknown) => {
        console.error('Failed to update issues:', error);
        setBulkError('Some statuses could not be saved. Please try again.');
      })
      .finally(() => setBulkStatus(null));
  };

  const clearFilters = () => {
    onScanFilterChange(null);
    setSeverity('');
    setQuery('');
  };

  if (register.state === 'loading' || register.state === 'idle') {
    return (
      <div className="app-scroll">
        <div className="app-issues-inner">
          <div className="app-skeleton app-issues-skeleton" role="status" aria-label="Loading issues">
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  if (register.state === 'error') {
    return (
      <div className="app-scroll">
        <div className="app-issues-inner app-issues-empty">
          <p>Issues could not be loaded.</p>
          <button type="button" className="app-action-button" onClick={onRetry}>Retry</button>
        </div>
      </div>
    );
  }

  const isRegisterEmpty = register.issues.length === 0;

  return (
    <div className="app-scroll">
      <div className="app-issues-inner">
        {isScanActive && latestScan && (
          <div className="app-issues-running">
            <AppPentestProgressWidget runId={latestScan.id} onFinished={onScanFinished} />
          </div>
        )}

        {isRegisterEmpty ? (
          !isScanActive && (
            <div className="app-issues-empty">
              <p>{register.reportScans.length > 0 ? 'No issues found.' : 'No findings yet.'}</p>
              <button type="button" className="app-action-button" onClick={onRunPentest}>
                <IconZap aria-hidden={true} />
                Run an assessment
              </button>
            </div>
          )
        ) : (
          <>
            <div className="app-tabs" role="tablist" aria-label="Issue status">
              {shownTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={statusTab === tab}
                  className={`app-tab${statusTab === tab ? ' active' : ''}`}
                  onClick={() => setStatusTab(tab)}
                >
                  {tab === 'all' ? 'All' : ISSUE_STATUS_LABELS[tab]}
                  <span className="app-tab-count">{counts[tab]}</span>
                </button>
              ))}
            </div>

            <div className="app-filters">
              <input
                className="app-filter-search"
                type="search"
                value={query}
                placeholder="Search issues"
                aria-label="Search issues"
                onChange={(event) => setQuery(event.target.value)}
              />

              {presentSeverities.length > 1 && (
                <select
                  className={`app-filter-select${severity ? ' active' : ''}`}
                  aria-label="Filter by severity"
                  value={severity}
                  onChange={(event) => setSeverity(event.target.value)}
                >
                  <option value="">All severities</option>
                  {presentSeverities.map((filter) => (
                    <option key={filter.id} value={filter.id}>{filter.label}</option>
                  ))}
                </select>
              )}

              {register.reportScans.length > 1 && (
                <select
                  className={`app-filter-select${scanFilter ? ' active' : ''}`}
                  aria-label="Filter by scan"
                  value={scanFilter ?? ''}
                  onChange={(event) => onScanFilterChange(event.target.value || null)}
                >
                  <option value="">All scans</option>
                  {register.reportScans.map((scan) => (
                    <option key={scan.id} value={scan.id}>
                      {formatScanDate(scan)}{scan.branch ? ` · ${scan.branch}` : ''}
                    </option>
                  ))}
                </select>
              )}

              {pdfScanId && (
                <a
                  className="app-filter-link"
                  href={getReportPdfUrl(pdfScanId)}
                  download={true}
                  aria-label={scanFilter ? 'Download this scan as PDF' : 'Download the latest scan as PDF'}
                  title={scanFilter ? 'Download this scan as PDF' : 'Download the latest scan as PDF'}
                >
                  <IconDownload aria-hidden={true} />
                </a>
              )}
            </div>

            {checkedIssues.length > 0 && (
              <div className="app-bulk" role="toolbar" aria-label="Selected issues">
                <span className="app-bulk-count">{checkedIssues.length} selected</span>
                <button type="button" className="app-bulk-action" disabled={bulkStatus !== null} onClick={() => applyBulk('acknowledged')}>
                  {bulkStatus === 'acknowledged' ? <span className="app-spinner" aria-hidden={true} /> : <IconCheckCircle aria-hidden={true} />}
                  Acknowledge
                </button>
                <button type="button" className="app-bulk-action" disabled={bulkStatus !== null} onClick={() => applyBulk('accepted')}>
                  {bulkStatus === 'accepted' ? <span className="app-spinner" aria-hidden={true} /> : <IconDismiss aria-hidden={true} />}
                  Dismiss
                </button>
                <button type="button" className="app-bulk-action" disabled={bulkStatus !== null} onClick={() => applyBulk(null)}>
                  {bulkStatus === 'open' && <span className="app-spinner" aria-hidden={true} />}
                  Reopen
                </button>
                <button type="button" className="app-bulk-action" onClick={() => onAsk(checkedIssues)}>
                  <IconChat aria-hidden={true} />
                  Ask
                </button>
                <button
                  type="button"
                  className="app-icon-button app-bulk-clear"
                  aria-label="Clear selection"
                  onClick={() => setCheckedKeys(new Set())}
                >
                  <IconClose aria-hidden={true} />
                </button>
              </div>
            )}
            {bulkError && <p className="app-widget-error app-bulk-error" role="alert">{bulkError}</p>}

            {visible.length === 0 ? (
              <div className="app-issues-empty compact">
                <p>
                  {hasFilters
                    ? 'No issues match these filters.'
                    : statusTab === 'all' ? 'No issues.' : `No ${ISSUE_STATUS_LABELS[statusTab].toLowerCase()} issues.`}
                </p>
                {hasFilters && <button type="button" className="app-text-button" onClick={clearFilters}>Clear filters</button>}
              </div>
            ) : (
              <ul className="app-issue-table" aria-label="Issues">
                <li className="app-issue-table-head">
                  <input
                    type="checkbox"
                    className="app-check"
                    aria-label="Select all"
                    checked={isAllChecked}
                    onChange={() => setCheckedKeys(isAllChecked ? new Set() : new Set(visible.map((issue) => issue.key)))}
                  />
                  <span aria-hidden={true}>Severity</span>
                  <span aria-hidden={true}>Issue</span>
                  <span aria-hidden={true}>Found</span>
                </li>
                {visible.map((issue) => (
                  <li
                    key={issue.key}
                    className={`app-issue-row is-${issue.status}${issue.key === selectedKey ? ' selected' : ''}${checkedKeys.has(issue.key) ? ' checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="app-check"
                      aria-label={`Select ${issue.cleanTitle}`}
                      checked={checkedKeys.has(issue.key)}
                      onChange={() => toggleChecked(issue.key)}
                    />
                    <button
                      type="button"
                      className="app-issue-row-main"
                      aria-current={issue.key === selectedKey ? 'true' : undefined}
                      onClick={() => onSelect(issue.key)}
                    >
                      <AppSeverity severity={issue.severity} />
                      <span className="app-issue-row-copy">
                        <span className="app-issue-row-line">
                          <span className="app-issue-row-title">{issue.cleanTitle}</span>
                          {/* On a status tab every row shares the status, so it is shown only under All. */}
                          {statusTab === 'all' && <AppStatusPill status={issue.status} />}
                        </span>
                        {issue.files[0] && <span className="app-issue-row-file">{issue.files[0]}</span>}
                      </span>
                      <span className="app-issue-row-seen" title={formatScanDate(issue.firstSeen)}>
                        {formatRelative(issue.firstSeen.createdAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AppIssuesView;
